import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Card, CardContent, CardHeader, CircularProgress, Paper, Stack, Typography, Tooltip, IconButton } from "@mui/material";
import { useTranslation } from "react-i18next";
import { OrderErrorViewModel } from "../../Types/Integration/OrderErrorViewModel ";
import { ListQuery } from "../../Types/ExportTypes";
import { useAuth, useLoading, useSnackbar } from "../../Context/Contexts";

import { Add as AddIcon, Queue as QueueIcon, Delete as DeleteIcon, Save as SaveIcon, Close as CancelIcon } from "@mui/icons-material";
import ResponsivePagination from "../ResponsivePagination";

interface GenericOrderErrorsProps {
    integrationId: number;
}

const GenericOrderErrors: React.FC<GenericOrderErrorsProps> = ({ integrationId }) => {
    const { authService } = useAuth();
    const { showLoading, hideLoading } = useLoading();
    const { showSnackbar } = useSnackbar();
    const { t } = useTranslation();

    const [query, setQuery] = useState<ListQuery>({
        page: 0,
        pageSize: 10,
        filter: "",
        filters: [],
    });
    const [totalCount, setTotalCount] = useState(0);
    const [errors, setErrors] = useState<OrderErrorViewModel[]>([]);
    const [isFetching, setIsFetching] = useState(false);

    const loadErrors = useCallback(async () => {
        setIsFetching(true);
        try {
            const response = await authService.post<{ items: OrderErrorViewModel[]; count: number }>(
                `/integrationApi/GetOrderErrors?integrationId=${integrationId}`,
                query
            );
            setErrors(response.items ?? []);
        } catch (error: any) {
            showSnackbar(
                `${t("dialog.failedToLoad", "Failed to load data")}: ${error?.response?.data ?? error?.message}`,
                "error"
            );
            setErrors([]);
        } finally {
            setIsFetching(false);
        }
    }, [authService, query, integrationId, showSnackbar, t]);

    useEffect(() => {
        if (!integrationId) return;
        loadErrors();
    }, [integrationId, loadErrors]);

    const formatDate = (value?: string | null) => {
        if (!value) return "-";
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return value;
        }
        return date.toLocaleString();
    };

    const handlePageChange = (newPage: number) => {
        setQuery({ ...query, page: newPage });
    };

    const handleRequeue = async (error: OrderErrorViewModel) => {
        showLoading();
        try {
            await authService.post(`/integrationApi/RequeueOrder?integrationId=${integrationId}`, error);
            showSnackbar(t("dialog.actionCompleted", "Action completed"), "success");
            await loadErrors();
        } catch (error: any) {
            showSnackbar(
                `${t("dialog.actionFailed", "Action failed")}: ${error?.response?.data ?? error?.message}`,
                "error"
            );
        } finally {
            hideLoading();
        }
    };

    const handleRemove = async (error: OrderErrorViewModel) => {
        if (!window.confirm(t("dialog.areYouSure"))) return;
        showLoading();
        try {
            await authService.post(`/integrationApi/RemoveOrderError?integrationId=${integrationId}`, error);
            showSnackbar(t("dialog.deletedSuccessfully"), "success");
            await loadErrors();
        } catch (error: any) {
            showSnackbar(
                `${t("dialog.deleteFailed")}: ${error?.response?.data ?? error?.message}`,
                "error"
            );
        } finally {
            hideLoading();
        }
    };

    const handleRequeueAll = async () => {
        if (!errors.length) return;
        showLoading();
        try {
            await authService.post(`/integrationApi/RequeueAllOrders?integrationId=${integrationId}`, {});
            showSnackbar(t("dialog.actionCompleted", "Action completed"), "success");
            await loadErrors();
        } catch (error: any) {
            showSnackbar(
                `${t("dialog.actionFailed", "Action failed")}: ${error?.response?.data ?? error?.message}`,
                "error"
            );
        } finally {
            hideLoading();
        }
    };

    return (
        <Card sx={{
            boxShadow: "none",
            backgroundColor: "transparent",
            border: "none"
        }}>
            <CardHeader
                title={t("common.orderErrors")}
                action={
                    <Button variant="contained" size="small" onClick={handleRequeueAll} disabled={!errors.length}>
                        {t("common.requeueAll", "Requeue all")}
                    </Button>
                }
            />
            <CardContent>
                {isFetching ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : errors.length === 0 ? (
                    <Typography color="text.secondary">
                            {t("common.noEntriesFound")}
                    </Typography>
                ) : (
                    <Stack spacing={2}>
                        {errors.map((error) => (
                            <Paper
                                key={String(
                                    error.id ?? `${error.externalOrderNo ?? "order"}-${error.createdAt ?? "created"}`
                                )}
                                sx={{ p: 2 }}
                            >
                                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={2}>
                                    <Box>
                                        <Typography variant="subtitle1">
                                            {error.externalOrderNo || error.externalOrderId || t("common.orderNo", "Order")}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
                                            {error.errorMessage ?? t("common.noInformation", "No error message provided")}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {t("common.retries", "Retries")}: {error.retries ?? 0} • {t("common.created", "Created")}: {formatDate(error.createdAt)}
                                            {error.updatedAt && ` • ${t("common.updated", "Updated")}: ${formatDate(error.updatedAt)}`}
                                        </Typography>
                                    </Box>
                                    <Stack direction="row" spacing={1} justifyContent="flex-end">

                                        <Tooltip title={t("common.requeue")}>
                                            <IconButton size="small" color="primary" onClick={() => handleRequeue(error)}>
                                                <QueueIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={t("common.delete")}>
                                            <IconButton size="small" color="error" onClick={() => handleRemove(error)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>


                                    </Stack>
                                </Stack>
                            </Paper>
                        ))}
                    </Stack>
                )}
                <ResponsivePagination
                    sx={{ mt: 2 }}
                    page={query.page}

                    pageSize={query.pageSize}

                    totalCount={totalCount}

                    onPageChange={handlePageChange}

                />
            </CardContent>
        </Card>
    );
};

export default GenericOrderErrors;
